import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProfileRelatedInfoBean } from '../../../beans/account/account-module.bean';
import { DataCarrierService } from '../../../services/core/data-carrier.service';
import { JwtHelper } from 'src/app/services/core/jwt-helper.service';
import { MessagingService } from '../../../services/messaging/messaging.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { APP_USER_PERMISSIONS } from '../../../constants/app-user-permissions.constants';
import { USER_LEVEL } from '../../../constants/user.level.constants';
import { AppointmentsService } from '../../../services/scheduling/appointment/appointments.service';
import { SimplifiedAppointmentsService } from '../../../services/scheduling/simplified-appointment/simplified-appointments.service';
@Component({
	selector: 'app-ehe-topbar',
	templateUrl: './topbar.component.html',
	styleUrls: ['./topbar.component.scss'],
})
export class TopbarComponent implements OnInit, OnDestroy {
	profileData: ProfileRelatedInfoBean;
	private upcomingAppointmentsCount?: number;

	inboxCount = 0;

	@Output() closeSidebar = new EventEmitter<void>();

	private _destroyed$: Subject<boolean> = new Subject<boolean>();

	private readonly MODULE_LEVEL = APP_USER_PERMISSIONS;
	private readonly USER_LEVEL = USER_LEVEL;

	constructor(
		private dataCarrierService: DataCarrierService,
		private jwtService: JwtHelper,
		public messagingService: MessagingService,
		private router: Router,
		private permissionsService: NgxPermissionsService,
		private appointmentsService: AppointmentsService
	) {}

	ngOnInit(): void {
		this.dataCarrierService
			.getChannel()
			.pipe(takeUntil(this._destroyed$))
			.subscribe((data: any) => {
				if (data?.profile) {
					this.profileData = data.profile;
					this.messagingService.setCurrentMessageCount({
						inbox: data.profile.unreadMessageCount ?? 0,
					});
				}
			});

		this.messagingService
			.getCurrentMessageCount()
			.pipe(takeUntil(this._destroyed$))
			.subscribe((count: { inbox?: number }) => {
				this.inboxCount = count?.inbox ?? 0;
			});

		// Hide "Book Exam" when the user has upcoming appointments.
		// This avoids relying solely on eligibleForPE, which can be stale on initial login redirects.
		// In simplified scheduling flows, `AppointmentsService` is provided by `SimplifiedAppointmentsService`,
		// which does not implement `getUpcomingAppointmentsCount()`.
		if (!(this.appointmentsService instanceof SimplifiedAppointmentsService)) {
			this.appointmentsService
				.getUpcomingAppointmentsCount()
				.pipe(takeUntil(this._destroyed$))
				.subscribe((count: any) => {
					if (typeof count === 'number') {
						this.upcomingAppointmentsCount = count;
					}
				});
		}
	}

	get fullName(): string {
		const first = this.profileData?.profileInfo?.firstName || '';
		const last = this.profileData?.profileInfo?.lastName || '';
		return `${first} ${last}`.trim();
	}

	get initials(): string {
		const first = this.profileData?.profileInfo?.firstName?.charAt(0) || '';
		const last = this.profileData?.profileInfo?.lastName?.charAt(0) || '';
		return (first + last).toUpperCase();
	}

	get memberStatus(): string {
		return this.profileData?.profileInfo?.userMatched ? 'Active' : 'Inactive';
	}

	get firstName(): string {
		return this.profileData?.profileInfo?.firstName || '';
	}

	get membershipDisplayLabel(): string {
		return this.profileData?.profileInfo?.client?.isIndividualGroup
			? 'EHE Membership'
			: 'Employer-Sponsored Membership';
	}

	get isMatchedMember(): boolean {
		return !!this.profileData?.profileInfo?.userMatched;
	}

	get isOnBookingOrPaymentPage(): boolean {
		const url = this.router.url;
		return url.includes('/scheduling/create/appointment/v3') || url.includes('/scheduling/create/appointment/review');
	}

	get isEligibleForExam(): boolean {
		// Avoid flicker: default to hidden until we know whether the user has upcoming appointments.
		// Once count is known, show only if it is 0.
		if (typeof this.upcomingAppointmentsCount === 'number') {
			return this.upcomingAppointmentsCount <= 0;
		}

		// If we don't have appointments count yet, keep hidden.
		// (This prevents showing then disappearing on initial dashboard load.)
		return false;
	}

	canShowHealthCoaching(): boolean {
		const p = this.permissionsService.getPermissions() || {};
		const hasAccess = !!(p[this.MODULE_LEVEL.HM] || p[this.MODULE_LEVEL.PW]);
		const hiddenByClient = !!p[this.USER_LEVEL.CLIENT_HIDE_HM];
		return hasAccess && !hiddenByClient;
	}

	bookExam(): void {
		this.router.navigate(['/scheduling/create/appointment/v3']);
	}

	navigateToProfile(): void {
		if (window.innerWidth < 992) {
			this.closeSidebar.emit();
		}
		this.router.navigate(['/account']);
	}

	ngOnDestroy(): void {
		this._destroyed$.next(true);
		this._destroyed$.unsubscribe();
	}
}
